import type { SportType } from '../sports.types';
import type { ISportDefinition } from './sport-definition.interface';

/**
 * Registry that maps SportType identifiers to their ISportDefinition implementations.
 *
 * Usage:
 *   SportDefinitionRegistry.register(new GolfDrivingSportDefinition());
 *   const def = SportDefinitionRegistry.get('golf-driving');
 *
 * All sport definitions are registered once at app startup via sports.plugin.ts.
 * The renderer and scene component access definitions only through this registry.
 */
export class SportDefinitionRegistry {
  private static readonly definitions = new Map<SportType, ISportDefinition>();

  /**
   * Register a sport definition.
   * Throws if the same SportType is registered twice (catches duplicates during HMR).
   */
  static register(definition: ISportDefinition): void {
    if (this.definitions.has(definition.id)) {
      throw new Error(
        `SportDefinition for '${definition.id}' is already registered.`
      );
    }
    this.definitions.set(definition.id, definition);
  }

  /**
   * Retrieve a sport definition by type.
   * Throws a descriptive error if the sport is not registered.
   */
  static get(type: SportType): ISportDefinition {
    const definition = this.definitions.get(type);
    if (!definition) {
      const available = Array.from(this.definitions.keys()).join(', ') || 'none';
      throw new Error(
        `No SportDefinition registered for '${type}'. ` +
        `Available: ${available}`
      );
    }
    return definition;
  }

  /** Returns true if a definition for the given type is registered. */
  static has(type: SportType): boolean {
    return this.definitions.has(type);
  }

  /** Returns all registered sport types, in registration order. */
  static getRegisteredTypes(): SportType[] {
    return Array.from(this.definitions.keys());
  }

  /**
   * Clear all registrations.
   * Used in unit tests only — never call in production code.
   */
  static clearForTesting(): void {
    this.definitions.clear();
  }
}
